import type { CallRecord } from '@/types';

/**
 * Call costs at InspireTel's standard rates (PortaBilling "Retail Docutec - Gold"):
 * per-second billing, R0.60/min to SA mobiles, R0.30/min to SA landlines.
 * Gold also has a few landline/non-geographic ranges at R0.32-R0.40; this uses the flat rates.
 */

export type DestinationCategory = 'mobile' | 'landline' | 'tollFree' | 'international' | 'other';

export interface CallRates {
  mobilePerMinute: number;
  landlinePerMinute: number;
}

export const STANDARD_RATES: CallRates = { mobilePerMinute: 0.6, landlinePerMinute: 0.3 };

export const CATEGORY_LABELS: Record<DestinationCategory, string> = {
  mobile: 'Mobile',
  landline: 'Landline',
  tollFree: 'Toll-free',
  international: 'International',
  other: 'Other',
};

export interface CallCost {
  category: DestinationCategory | null; // null: not an outbound call
  billableSeconds: number;
  ratePerMinute: number | null; // null: not priced (international/other)
  cost: number; // rand, rounded to cents
}

/** The dialled number of an outbound call, as digits (with "+" kept). */
export function dialledNumber(record: CallRecord): string {
  const raw = String(record.call_to_number || record.call_to || '').trim();
  const bracketed = raw.match(/<([^>]+)>\s*$/);
  const value = bracketed ? bracketed[1] : raw;
  return (value.trim().startsWith('+') ? '+' : '') + value.replace(/\D/g, '');
}

/** Classify a dialled number using South African numbering (06x/07x/081-084 mobile). */
export function classifyDestination(number: string): DestinationCategory {
  let digits = number.replace(/\D/g, '');
  const plus = number.trim().startsWith('+');

  if (plus || digits.startsWith('00')) {
    digits = digits.replace(/^00/, '');
    if (!digits.startsWith('27')) return 'international';
    digits = `0${digits.slice(2)}`;
  } else if (digits.startsWith('27') && digits.length === 11) {
    digits = `0${digits.slice(2)}`;
  }

  if (!/^0[1-9]\d{8,}$/.test(digits)) return 'other';
  if (/^0[67]/.test(digits) || /^08[1-4]/.test(digits)) return 'mobile';
  if (digits.startsWith('080')) return 'tollFree';
  return 'landline'; // 01x-05x geographic, 085-089 non-geographic
}

export function isOutboundCall(record: CallRecord) {
  return record.call_type === 'Outbound';
}

export function callCost(record: CallRecord, rates: CallRates): CallCost {
  if (!isOutboundCall(record)) {
    return { category: null, billableSeconds: 0, ratePerMinute: null, cost: 0 };
  }
  const category = classifyDestination(dialledNumber(record));
  const billableSeconds = Math.max(0, Math.round(record.talk_duration || 0));
  const ratePerMinute =
    category === 'mobile'
      ? rates.mobilePerMinute
      : category === 'landline'
        ? rates.landlinePerMinute
        : category === 'tollFree'
          ? 0
          : null;
  // Round half-up to the cent; the epsilon absorbs float error (61 s x R0.30 = 30.5c -> 31c)
  const cost = ratePerMinute
    ? Math.round((billableSeconds * ratePerMinute * 100) / 60 + 1e-9) / 100
    : 0;
  return { category, billableSeconds, ratePerMinute, cost };
}

/** Extension number that placed an outbound call, e.g. "207" from "Carina<207>". */
export function callingExtension(record: CallRecord): string | null {
  const fromNumber = String(record.call_from_number || '').trim();
  if (/^\d{2,6}$/.test(fromNumber)) return fromNumber;
  const bracketed = String(record.call_from || '').match(/<(\d{2,6})>\s*$/);
  if (bracketed) return bracketed[1];
  const plain = String(record.call_from || '').trim();
  return /^\d{2,6}$/.test(plain) ? plain : null;
}

export interface CostTotals {
  calls: number;
  seconds: number;
  cost: number;
}

export interface CostSummary {
  byCategory: Record<DestinationCategory, CostTotals>;
  total: CostTotals; // all outbound calls with talk time
  unpricedCalls: number; // international/other calls with talk time
}

const emptyTotals = (): CostTotals => ({ calls: 0, seconds: 0, cost: 0 });

/** Totals over answered outbound calls (talk time > 0). Costs are summed per call, in cents. */
export function summariseCosts(records: CallRecord[], rates: CallRates): CostSummary {
  const byCategory = {
    mobile: emptyTotals(),
    landline: emptyTotals(),
    tollFree: emptyTotals(),
    international: emptyTotals(),
    other: emptyTotals(),
  } satisfies Record<DestinationCategory, CostTotals>;
  const total = emptyTotals();
  let unpricedCalls = 0;

  for (const record of records) {
    const result = callCost(record, rates);
    if (!result.category || result.billableSeconds === 0) continue;
    for (const totals of [byCategory[result.category], total]) {
      totals.calls += 1;
      totals.seconds += result.billableSeconds;
      totals.cost = Math.round((totals.cost + result.cost) * 100) / 100;
    }
    if (result.ratePerMinute === null) unpricedCalls += 1;
  }

  return { byCategory, total, unpricedCalls };
}

export function formatRand(value: number): string {
  return `R${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
